import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Fab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Model, CustomError } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { saveSurveyResult, generateTeaser } from "api/cansurvey/surveyApi";
import { useSurvey, useToast } from "hooks/survey";
import type { TeaserData } from "types/survey";

// ONE-OFF: AI Readiness Survey token. Remove when campaign ends.
const AI_READINESS_SURVEY_TOKEN = "B2976263-3F3B-4355-800E-B0F28D59A918";

function TakeSurvey() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const surveyId = searchParams.get("surveyId");
  const utmSource = searchParams.get("utm_source");
  const surveyIdentifier = token || surveyId;
  const [submitted, setSubmitted] = useState(false);
  const { surveyJson, loading, error } = useSurvey();
  const { toast, showToast, closeToast } = useToast();

  // ONE-OFF: AI Readiness Survey teaser state. Remove when campaign ends.
  const isAiReadinessSurvey = token?.toUpperCase() === AI_READINESS_SURVEY_TOKEN;
  const [teaserOpen, setTeaserOpen] = useState(false);
  const [teaserData, setTeaserData] = useState<TeaserData | null>(null);
  const teaserShownRef = useRef(false);

  // Check if survey has already been submitted using cookie
  useEffect(() => {
    if (surveyIdentifier) {
      const submittedCookie = Cookies.get(`survey_${surveyIdentifier}_submitted`);
      if (submittedCookie) {
        setSubmitted(true);
      }
    }
  }, [surveyIdentifier]);

  // Memoize survey model to prevent recreation on every render
  const survey = useMemo(() => {
    if (!surveyJson) {
      return null;
    }
    const model = new Model(surveyJson);

    model.applyTheme({
      cssVariables: {
        "--sjs-general-backcolor-dim": "transparent",
      },
    });

    // Handle page navigation with answer grading
    model.onCurrentPageChanging.add((sender, options) => {
      // Only validate when moving forward
      if (!options.isNextPage) return;
      const currentPage = sender.currentPage;
      const questions = currentPage.questions;
      let hasIncorrect = false;
      for (const question of questions) {
        // Clear previous errors
        question.clearErrors();
        // Skip if not gradeable
        const hasCorrectAnswer = question.correctAnswer !== undefined 
            && question.correctAnswer !== null;
        if (!question.isRequired || !hasCorrectAnswer) {
          continue;
        }
        try {
          if (!question.isAnswerCorrect()) {
            question.addError(new CustomError("Incorrect answer. Please try again."));
            hasIncorrect = true;
          }
        } catch (e) {
          // Skip grading for unrecognized question types or errors
          console.warn(`Grading skipped for question "${question.name}":`, e);
        }
      }
      if (hasIncorrect) {
        options.allow = false;
        return;
      }

      // ONE-OFF: AI Readiness Survey teaser.
      // When navigating from section 3 (page index 2) to section 4 (page index 3),
      // silently fetch a personalized AI teaser in the background. The drawer opens
      // automatically once the teaser is ready — navigation is not blocked.
      // Remove when campaign ends.
      if (
        isAiReadinessSurvey &&
        options.isNextPage &&
        sender.currentPageNo === 2 &&
        !teaserShownRef.current
      ) {
        teaserShownRef.current = true;

        // Resolve raw SurveyJS values (e.g. "Item 1") to human-readable
        // question titles and answer text before sending to the AI.
        const resolvedAnswers: Record<string, string> = {};
        for (const [questionName, rawValue] of Object.entries(sender.data)) {
          const question = sender.getQuestionByName(questionName);
          if (!question) continue;
          const title = question.title || questionName;
          const displayValue = question.displayValue;
          resolvedAnswers[title] = typeof displayValue === 'string'
            ? displayValue
            : JSON.stringify(displayValue);
        }

        generateTeaser(resolvedAnswers)
          .then((data) => {
            if (data) {
              setTeaserData(data);
              setTeaserOpen(true);
            }
          })
          .catch(() => {
            // AI failed — do nothing, user continues normally
          });
      }
    });

    // Handle survey completion
    model.onComplete.add(async (sender) => {
      if (!token && !surveyId) {
        showToast("Survey identifier is missing. Cannot submit response.", "error");
        return;
      }

      try {
        const resultContent = JSON.stringify(sender.data);
        const identifier = token ? { token } : { surveyId: Number(surveyId) };
        await saveSurveyResult(identifier, resultContent, utmSource || undefined);

        // Set a cookie to mark the survey as submitted (expires in 1 day)
        // This is a soft prevention measure, not hard security
        Cookies.set(`survey_${surveyIdentifier}_submitted`, "true", { expires: 1 });

        showToast("Survey submitted successfully!", "success");
        setSubmitted(true);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to submit the survey. Please try again.";
        showToast(errorMessage, "error");
      }
    });

    return model;
  }, [surveyJson, token, surveyId, surveyIdentifier, showToast, isAiReadinessSurvey]);

  // ONE-OFF: Teaser drawer toggle. Remove when campaign ends.
  const handleTeaserClose = useCallback(() => setTeaserOpen(false), []);
  const handleTeaserReopen = useCallback(() => setTeaserOpen(true), []);

  // Show loading spinner while fetching survey
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If survey has already been submitted, show thank-you message
  if (submitted) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={2}>
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main" }} />
        <Typography variant="h4">Thank you!</Typography>
        <Typography variant="body1" color="text.secondary">
          Your response has been submitted. You can close this page.
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error || !surveyJson || !survey) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6" color="error">
          {error || "Failed to load the survey. Please try again later."}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Survey model={survey} />

      {/* ONE-OFF: AI Readiness Survey teaser dialog. Remove when campaign ends. */}
      <Dialog
        open={teaserOpen}
        onClose={handleTeaserClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderTop: 4, borderColor: "secondary.main" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesomeIcon color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                {teaserData?.title || "Your Instant AI Analysis"}
              </Typography>
            </Box>
            <IconButton onClick={handleTeaserClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Challenges section */}
          {teaserData?.challenges && teaserData.challenges.length > 0 && (
            <Box mb={2}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningAmberIcon sx={{ color: "warning.main" }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Challenges We Spotted
                </Typography>
              </Box>
              <List dense disablePadding>
                {teaserData.challenges.map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.2 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <FiberManualRecordIcon sx={{ fontSize: 8, color: "secondary.main" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2">{item}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Recommendation section */}
          {teaserData?.recommendation && (
            <Box mb={2}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LightbulbIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Quick Win
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ pl: 1, lineHeight: 1.7 }}>
                {teaserData.recommendation}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* CTA teaser line */}
          {teaserData?.cta && (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
              sx={{ textAlign: "center" }}
            >
              {teaserData.cta}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: "text.disabled" }} />
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem", fontStyle: "italic" }}>
              AI Insights powered by CANai
            </Typography>
          </Box>
          <Button variant="contained" color="secondary" onClick={handleTeaserClose}>
            Get My AI-Readiness Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* ONE-OFF: Floating button to reopen teaser dialog. Remove when campaign ends. */}
      {teaserData && !teaserOpen && (
        <Fab
          color="secondary"
          size="medium"
          onClick={handleTeaserReopen}
          sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}
        >
          <AutoAwesomeIcon />
        </Fab>
      )}

      {/* Snackbar for toast notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeToast} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TakeSurvey;
